import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/**
 * Trial access gate — DISABLED.
 *
 * The 15-min / 24h survey wall has been removed. Anyone with an email
 * stays in forever; no modal blocks them. We keep this component so the
 * `useTrackSectionVisits` and `useHeartbeat` hooks below can still mount
 * from Layout — those drive admin analytics (sections explored, active
 * time, last_heartbeat_at), not user-facing gating.
 *
 * Survey is still available voluntarily from the dashboard / footer.
 */

export default function TrialAccessGate() {
    // The survey wall has been removed — anyone with an email stays in,
    // forever. We keep the component mounted (and the hooks `useHeartbeat`
    // + `useTrackSectionVisits` below) so analytics keep flowing, but
    // there's no longer any UI that blocks the user. Survey is still
    // available voluntarily from the dashboard / footer.
    //
    // All server-side fields (active_time_seconds, trial_started_at,
    // feedback_submitted_at, signed_in_after_survey_at) are still tracked
    // — so we have the data if the wall ever needs to come back.
    return null;
}

// ---- Section-visit tracker, mounted from Layout ----

const SECTION_PATH_MAP = {
    "/dashboard": "dashboard",
    "/chat": "chat",
    "/guidance": "guidance",
    "/conversations": "hard-talks",
    "/behavior": "behavior",
    "/burnout": "burnout",
    "/resources": "resources",
    "/help-finder": "resources",
    "/patient": "patient-profile",
    "/patient-profile": "patient-profile",
    "/roadmap": "roadmap",
    "/navigator": "navigator",
    "/care-network": "care-network",
    "/family": "family",
    "/appointment": "appointment",
    "/education": "education",
    "/feedback": "feedback",
};

export function useTrackSectionVisits(api) {
    const { user, refresh } = useAuth();
    const location = useLocation();

    useEffect(() => {
        if (!user || user === false) return;
        if (user.role === "admin") return;
        // Trial users hit the 3-section survey wall via this same array.
        // Once they've submitted feedback, we stop tracking. Testers, however,
        // keep counting so the admin can see which parts of the tour they
        // actually opened during the study.
        if (!user.tester_code && user.feedback_submitted_at) return;

        const section = SECTION_PATH_MAP[location.pathname];
        if (!section) return;

        const visited = user.sections_visited || [];
        if (visited.includes(section)) return;

        (async () => {
            try {
                await api.post("/me/section-visit", { section });
                await refresh();
            } catch (_e) { /* no-op */ }
        })();
    }, [user, location.pathname, api, refresh]);
}


// ---- Heartbeat / active-time tracker ----
//
// Increments the server-side `active_time_seconds` field while the user is
// genuinely using the app:
//   - tab is foregrounded (document.visibilityState === "visible")
//   - some real input event (mouse / key / touch / scroll) fired in the
//     last 60 seconds
//
// We tick a local counter every second under those conditions, then flush it
// to the backend every 30 seconds (or sooner on tab-hide / unmount). Server
// clamps a single delta at 60s to stop replay-style inflation.
//
// Idle-in-background tab contributes zero. Admins and not-yet-logged-in
// visitors are ignored.

const HEARTBEAT_FLUSH_MS = 30 * 1000;
const ACTIVITY_IDLE_MS = 60 * 1000;
const TICK_MS = 1000;

export function useHeartbeat(api) {
    const { user, refresh } = useAuth();

    useEffect(() => {
        if (!user || user === false) return undefined;
        if (user.role === "admin") return undefined;

        let lastActivity = Date.now();
        let pendingSeconds = 0;
        let stopped = false;

        const bumpActivity = () => { lastActivity = Date.now(); };
        const activityEvents = ["mousedown", "mousemove", "keydown", "touchstart", "scroll", "wheel"];
        activityEvents.forEach((ev) => window.addEventListener(ev, bumpActivity, { passive: true }));

        const tickId = setInterval(() => {
            if (stopped) return;
            if (document.visibilityState !== "visible") return;
            if (Date.now() - lastActivity > ACTIVITY_IDLE_MS) return;
            pendingSeconds += 1;
        }, TICK_MS);

        const flush = async (sync = false) => {
            const delta = pendingSeconds;
            pendingSeconds = 0;
            // Nothing to report → no POST and, critically, NO refresh().
            // The old `sync` path posted a zero delta on unmount and then
            // refreshed auth — refresh creates a new user object, which
            // re-runs this effect (deps include `user`), whose cleanup
            // flushed again… an infinite POST/refresh loop that tripped
            // the platform rate limiter and broke every dashboard fetch.
            if (delta <= 0) return;
            try {
                await api.post("/me/heartbeat", { delta_seconds: delta });
                // Refresh so TrialAccessGate sees the updated active_time_seconds.
                await refresh();
            } catch (_e) { /* offline / network blip — drop, we'll retry next interval */ }
        };
        const flushId = setInterval(flush, HEARTBEAT_FLUSH_MS);
        const onHide = () => { if (document.visibilityState === "hidden") flush(); };
        document.addEventListener("visibilitychange", onHide);

        return () => {
            stopped = true;
            clearInterval(tickId);
            clearInterval(flushId);
            activityEvents.forEach((ev) => window.removeEventListener(ev, bumpActivity));
            document.removeEventListener("visibilitychange", onHide);
            // Best-effort final flush on unmount.
            flush(true);
        };
    }, [user, api, refresh]);
}
